'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import {
  ToolSharingState,
  ToolType,
  SharedItem,
  hydrateToolSharingState,
  shareTools,
} from '@/store/slices/toolSharingSlice';
import { RootState } from '@/store/store';
import { CALLER_DEFAULT_UPLOAD, mapLegacyPath } from '@/lib/files/paths';

/**
 * Tool Sharing Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for tool sharing state.
 * - Debounced writes (500ms) to prevent rate limiting
 * - Hydrates from Supabase on mount
 * - Last-write-wins conflict resolution with timestamps
 *
 * Pattern follows useNotesSync.ts
 */

const SYNC_DEBOUNCE_MS = 500;

/**
 * Tools a NEW call starts out sharing with the caller. Owner, 2026-09-23:
 * "Background shared as a tool to the caller -> it should be enabled by
 * default!" Applied only when the call has no saved sharing state, so an
 * officer who unshared it keeps it unshared across reloads and reconnects.
 */
export const DEFAULT_SHARED_TOOLS: readonly ToolType[] = ['background'];

interface UseToolSharingSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface ToolSharingSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

// Database schema for tool sharing content
interface DbToolSharingContent {
  shared_tools: ToolType[];
  shared_files: string[];
  shared_items: SharedItem[];
  caller_read_only: boolean;
  caller_upload_folder: string;
  file_browser_access_token: string | null;
  last_updated: number;
}

/**
 * Convert Redux ToolSharingState to DB schema format
 */
function toDbFormat(state: ToolSharingState): DbToolSharingContent {
  return {
    shared_tools: state.sharedTools,
    shared_files: state.sharedFiles,
    shared_items: state.sharedItems,
    caller_read_only: state.callerReadOnly,
    caller_upload_folder: state.callerUploadFolder,
    file_browser_access_token: state.fileBrowserAccessToken,
    last_updated: state.lastUpdated,
  };
}

/**
 * Convert DB format back to Redux ToolSharingState (partial - fields we persist)
 */
function fromDbFormat(content: DbToolSharingContent): Partial<ToolSharingState> {
  return {
    sharedTools: content.shared_tools || [],
    sharedFiles: content.shared_files || [],
    sharedItems: content.shared_items || [],
    callerReadOnly: content.caller_read_only || false,
    callerUploadFolder: mapLegacyPath(content.caller_upload_folder || CALLER_DEFAULT_UPLOAD),
    fileBrowserAccessToken: content.file_browser_access_token || null,
    lastUpdated: content.last_updated || 0,
    // Derive these from sharedTools
    isSharing: (content.shared_tools || []).length > 0,
    activeTool: (content.shared_tools || []).length > 0 ? content.shared_tools[0] : null,
  };
}

/**
 * Hash the durable tool-sharing fields. Shared by the debounced writer (skip
 * redundant syncs) AND the reconnect dirty-guard (#725) so both hash the exact
 * same shape. NOTE: the local-only `tokenMintRequestCounter` is intentionally
 * excluded (it is never persisted); the forced-reload path preserves it
 * separately so a pending mint isn't lost.
 */
function hashToolSharing(s: ToolSharingState): string {
  return JSON.stringify({
    tools: s.sharedTools,
    files: s.sharedFiles,
    items: s.sharedItems.map(i => ({ p: i.path, t: i.type })),
    ro: s.callerReadOnly,
    folder: s.callerUploadFolder,
    token: s.fileBrowserAccessToken,
    updated: s.lastUpdated,
  });
}

/**
 * Hook to sync tool sharing state to Supabase in real-time
 */
export function useToolSharingSync(
  options: UseToolSharingSyncOptions,
): ToolSharingSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const toolSharingState = useSelector((state: RootState) => state.toolSharing);

  // Live mirror of current Redux tool-sharing so the reconnect dirty-guard (#725)
  // can read it (incl. tokenMintRequestCounter) without widening deps.
  const toolSharingRef = useRef(toolSharingState);
  toolSharingRef.current = toolSharingState;

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const stateRef = useReactiveRef<ToolSharingSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useToolSharingSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync tool sharing state to Supabase
   */
  const syncToSupabase = useCallback(async (state: ToolSharingState) => {
    if (!enabled || !callId) return;

    // Compute hash to avoid redundant syncs
    const hash = hashToolSharing(state);

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useToolSharingSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      // Convert to DB format
      const dbContent = toDbFormat(state);

      // Upsert tool sharing state
      const { error } = await supabase
        .from('tool_sharing')
        .upsert({
          call_id: dbCallId,
          content: dbContent,
        }, {
          onConflict: 'call_id',
        });

      if (error) {
        throw error;
      }

      lastSyncedHashRef.current = hash;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useToolSharingSync', 'Synced tool sharing to Supabase', {
        callId,
        sharedTools: state.sharedTools,
        sharedItemsCount: state.sharedItems.length,
      });
    } catch (err: unknown) {
      logger.error('useToolSharingSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  /**
   * Load tool sharing state from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasLoadedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    // #725 reconnect dirty-guard: on a FORCED re-sync, preserve the operator's
    // un-flushed sharing change rather than clobbering it with DB. The pending
    // debounced write flushes it. null baseline → never loaded → proceed (Obs1).
    if (
      opts?.force &&
      lastSyncedHashRef.current !== null &&
      hashToolSharing(toolSharingRef.current) !== lastSyncedHashRef.current
    ) {
      logger.info('useToolSharingSync', 'Skipping reconnect re-sync — un-flushed local sharing change pending');
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    hasLoadedRef.current = true;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        // No call row yet: a brand-new call, so nothing was ever unshared.
        if (!opts?.force && mountedRef.current) dispatch(shareTools([...DEFAULT_SHARED_TOOLS]));
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      const { data: toolSharingData, error } = await supabase
        .from('tool_sharing')
        .select('content')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (toolSharingData?.content && mountedRef.current) {
        const content = toolSharingData.content as DbToolSharingContent;
        const dbState = fromDbFormat(content);

        // Build full state with defaults
        const fullState: ToolSharingState = {
          sharedTools: dbState.sharedTools || [],
          activeTool: dbState.activeTool ?? (dbState.sharedTools?.[0] || null),
          isSharing: dbState.isSharing ?? (dbState.sharedTools?.length || 0) > 0,
          callerReadOnly: dbState.callerReadOnly ?? false,
          sharedFiles: dbState.sharedFiles || [],
          sharedItems: dbState.sharedItems || [],
          callerUploadFolder: dbState.callerUploadFolder || CALLER_DEFAULT_UPLOAD,
          fileBrowserAccessToken: dbState.fileBrowserAccessToken || null,
          callerHasMinimized: false,
          callerActiveTab: 'chat',
          newlySharedTools: [],
          lastUpdated: dbState.lastUpdated || 0,
          // Local-only counter; not persisted in DB. Reset to 0 on a MOUNT
          // hydrate so a stale DB row can't synthesize a phantom mint request,
          // but PRESERVE the live counter on a forced reconnect reload (#725)
          // so an in-session token mint isn't silently dropped.
          tokenMintRequestCounter: opts?.force ? toolSharingRef.current.tokenMintRequestCounter : 0,
        };

        dispatch(hydrateToolSharingState(fullState));
        lastSyncedHashRef.current = hashToolSharing(fullState);

        logger.info('useToolSharingSync', 'Loaded from Supabase', {
          sharedTools: fullState.sharedTools,
          sharedItemsCount: fullState.sharedItems.length,
        });
      } else if (!toolSharingData?.content && !opts?.force && mountedRef.current) {
        // The call has never saved a sharing state: start with the defaults.
        dispatch(shareTools([...DEFAULT_SHARED_TOOLS]));
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useToolSharingSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch]);

  // #725: force a re-sync from Supabase on LiveKit reconnect (dirty-guard above
  // preserves an un-flushed local sharing change + the token-mint counter).
  // isReloadingRef collapses back-to-back reconnects into one in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Debounced sync when tool sharing state changes
  useEffect(() => {
    if (!enabled || !callId) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase(toolSharingState);
    }, debounceMs);
  }, [enabled, callId, toolSharingState, debounceMs, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate tool sharing state from storage
 */
export function useToolSharingHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const supabase = createClient();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (callData?.id) {
        const { data: toolSharingData } = await supabase
          .from('tool_sharing')
          .select('content')
          .eq('call_id', callData.id)
          .maybeSingle();

        if (toolSharingData?.content) {
          const content = toolSharingData.content as DbToolSharingContent;
          const dbState = fromDbFormat(content);
          const fullState: ToolSharingState = {
            sharedTools: dbState.sharedTools || [],
            activeTool: dbState.activeTool ?? (dbState.sharedTools?.[0] || null),
            isSharing: dbState.isSharing ?? (dbState.sharedTools?.length || 0) > 0,
            callerReadOnly: dbState.callerReadOnly ?? false,
            sharedFiles: dbState.sharedFiles || [],
            sharedItems: dbState.sharedItems || [],
            callerUploadFolder: dbState.callerUploadFolder || CALLER_DEFAULT_UPLOAD,
            fileBrowserAccessToken: dbState.fileBrowserAccessToken || null,
            callerHasMinimized: false,
            callerActiveTab: 'chat',
            newlySharedTools: [],
            lastUpdated: dbState.lastUpdated || 0,
            // Local-only counter; not persisted in DB. Reset on hydrate
            // so a stale DB row can't synthesize a phantom mint request.
            tokenMintRequestCounter: 0,
          };
          dispatch(hydrateToolSharingState(fullState));
          logger.info('useToolSharingHydration', 'Hydrated from Supabase', {
            sharedTools: fullState.sharedTools,
          });
        }
      }

      hydratedRef.current = true;
    } catch (err: unknown) {
      logger.error('useToolSharingHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useToolSharingSync;
